// With a combination of json-stable-stringify + object-hash, the same data will have
// the same checksum every time. Dict order in vanilla JS serialization does
// not guarantee any ordering. This provides us with something akin to a mutex
// across the transport, but still lets our transport layer be stateless.
import axios from 'axios';
import { WEBSITE_API_URL } from 'consts';
import stringify from 'json-stable-stringify';
import { flow, makeAutoObservable, toJS } from 'mobx';
import hash from 'object-hash';

import { getLeadingBidOptionLabel, getTime, normalizeLabelForBg } from 'utils';

// Prevent recursive stringification
const filteredKeys = new Set([
  'rootStore',
  'lastReceivedHash',
  'updating',
  'hasHydrated',
  'plain',
  'stringified',
  'hash',
  'obs',
  // NR8 had sync issues, recommended by Sonnet 4.6
  // https://claude.ai/share/909e30c6-8414-4376-9c75-3eb3b512c727
  // 'nextGame',
  // 'prize',
]);

const RTMPScenes = [
  '!RTMP SOURCES 01',
  '!RTMP SOURCES 02',
  '!RTMP SOURCES 03',
  '!RTMP SOURCES 04',
  '!RTMP SOURCES 05',
  '!RTMP SOURCES 06',
  '!RTMP SOURCES 07',
  '!RTMP SOURCES 08',
];

const RTMPSources = [
  'M - RTMP01',
  'M - RTMP02',
  'M - RTMP03',
  'M - RTMP04',
  'M - RTMP05',
  'M - RTMP06',
  'M - RTMP07',
  'M - RTMP08',
];

// In miliseconds
const RAFFLE_DURATION_MIN = 5_000;
const RAFFLE_DURATION_MAX = 8_000;

class Store {
  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  // Internal values
  lastReceivedHash = '';
  updating = false;
  hasHydrated = false;
  lastEventUpdate = null;
  campaign = {};
  hackTheStream = true;
  frameType = 'SuperMetroid';
  backgroundType = 'SuperMetroid';
  /** Full source names from OBS scene !BACKGROUNDS (e.g. "!BG : SuperMetroid") */
  backgroundSourceNames = [];

  // OBSERVABLE VARIABLES
  timerOffset = { hours: 0, minutes: 0, seconds: 0 };
  lang = 'EN';
  currentGame = {
    title: 'Pre-Show',
    subtitle: '',
    category: 'Stream%',
    platform: 'Twitch',
    estimate: '00:50:00',
    extra: '',
  };
  nextGame = [
    {
      title: 'Chrono Trigger',
      subtitle: '',
      category: 'New PC Update',
      platform: 'PC',
      estimate: '04:20:69',
      extra: '',
    },
    {
      title: 'Next Game Name 2',
      subtitle: 'Next Game Subtitle 2',
      category: 'Next Game Category 2',
      platform: 'Next Game Platform 2',
      estimate: '04:20:69',
      extra: 'Extra Game Information 3',
    },
  ];
  gameName = 'Chrono Trigger';
  gameSubtitle = '';
  category = 'New PC Update';
  platform = 'PC';
  estimate = '04:20:69';
  estimate2 = '04:20:69';
  host = 'HostName';
  couch = ['CouchName', 'CouchName2'];
  countdown = { minutes: 0 };
  cdtimer = { start: null, end: null };
  extraInformation = '';
  players = 1;
  runners = [
    {
      name: 'noresetspeedrun',
      endTime: null,
    },
    {
      name: 'New Runner',
      endTime: null,
    },
    {
      name: 'New Runner',
      endTime: null,
    },
    {
      name: 'New Runner',
      endTime: null,
    },
    {
      name: 'New Runner',
      endTime: null,
    },
    {
      name: 'New Runner',
      endTime: null,
    },
    {
      name: 'New Runner',
      endTime: null,
    },
    {
      name: 'New Runner',
      endTime: null,
    },
  ];
  runnerCount = 1;
  timer = { start: null, end: null };

  nextGameName = 'Chrono Trigger';
  nextCategory = 'New PC Update';
  nextPlatform = 'PC';
  nextRunner = 'Shokushu';
  nextGameName2 = 'Next Game';
  nextCategory2 = 'Next Category';
  nextPlatform2 = 'Next Platform';
  nextRunner2 = 'Next Runner';
  nextHost = 'Next Host';
  nextCouch = 'Next Couch';

  // HORARO
  horaroId = '2';
  horaroData = {
    items: [],
  };
  upcomingData = {
    items: [],
  };
  selectedRunIdx = 0;
  currentRunIdx = 0;
  rtmpURL = 'rtmp://rtmp.noresetspeed.run/stream/';

  showUpcomingRuns = false;

  test = {
    ip: '127.0.0.1',
    port: '4455',
    address: `127.0.0.1:4455`,
    password: '',
    connected: false,
  };

  donationCount = {
    currentTotal: 0.0,
    previousTotal: 0.0,
    lastDonation: 0.0,
  };

  obs = {
    socket: null,
    info: {},
    sceneItemIdCache: {},
    inputs: [],
    audio: [
      { name: 'Discord', muted: false },
      { name: 'Music', muted: false },
      { name: 'Mic', muted: false },
    ],
    rtmp: [
      { name: 'RTMP01', muted: false },
      { name: 'RTMP02', muted: true },
      { name: 'RTMP03', muted: true },
      { name: 'RTMP04', muted: true },
      { name: 'RTMP05', muted: false },
      { name: 'RTMP06', muted: true },
      { name: 'RTMP07', muted: true },
      { name: 'RTMP08', muted: true },
    ],
    selected: {
      current: 0,
      next: 0,
    },
    obsninja: [
      { obsname: '', name: 'GROUP A', muted: false, visible: true },
      { obsname: '', name: 'GROUP B', muted: true, visible: false },
      { obsname: '', name: 'HOST A', muted: true, visible: false },
      { obsname: '', name: 'HOST B', muted: true, visible: false },
    ],
    ad: [
      { name: 'AD 1', muted: false, visible: true },
      { name: 'AD 2', muted: true, visible: false },
      { name: 'AD 3', muted: true, visible: false },
      { name: 'AD 4', muted: true, visible: false },
      { name: 'AD 5', muted: true, visible: false },
    ],
    cam: [
      { name: 'Host', visible: true },
      { name: 'Wide', visible: true },
      { name: 'Runners', visible: true },
      { name: 'Mask', visible: true }, // ALWAYS LAST!!!!!!
    ],
  };

  volRetroTopPlayers = [];

  fundky = {
    id: 2, // 1635 = NoReset5
    pollid: 103, // 89 - 90 - 91 = NoReset5
    frameid: 106, // 92 - 93 - 94 = NoReset5
    amount: 0.0,
    background: '',
    polls: [],
    milestones: [],
    donations: [],
    prizes: [],
    donationStatus: {},
    donationsHidden: {},
    bidwarStatus: {},
    milestoneStatus: [],
    backgroundPoll: [],
    backgroundOptions: [],
    framePoll: [],
    frameOptions: [],
  };

  raffle = {
    names: [],
    spinning: false,
    winner: '',
    winnerId: null,
    duration: 5,
    prize: 0,
    minDonation: 0,
    winners: [],
    mode: 'donations_only',
    minDonationRequired: false,
    selectedRunIds: [],
    localRaffle: false,
    ticketScope: 'current_day',
    excludePreviousWinnersForPrize: false,
  };

  sponsors = [];

  setDuration = () => {
    this.raffle.duration =
      Math.floor(Math.random() * RAFFLE_DURATION_MAX - RAFFLE_DURATION_MIN + 1) +
      RAFFLE_DURATION_MIN;
  };

  setNames = names => (this.raffle.names = names);

  setMinDonation = amount => (this.raffle.minDonation = amount);

  loadRaffleWinners = async eventId => {
    if (eventId == null || eventId === '') return;
    try {
      const { data } = await axios.get(`${WEBSITE_API_URL}/api/v2/raffles`, {
        params: { event: eventId },
      });
      const list = Array.isArray(data) ? data : (data?.results ?? []);
      this.raffle.winners = list;
    } catch {
      this.raffle.winners = [];
    }
  };

  updateRaffleList = async csrfToken => {
    if (!this.raffle.winner) return;

    const eventId = this.fundky?.id ?? '';
    if (!eventId) return;

    const response = await axios.post(
      `${WEBSITE_API_URL}/api/v2/raffles/`,
      {
        event: eventId,
        name: this.raffle.winner,
        prize: this.raffle.prize,
        donation: this.raffle.winnerId,
      },
      {
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
        },
        withCredentials: true,
      },
    );

    console.info('Raffle list updated');
    this.raffle.winners = [response.data, ...this.raffle.winners];
  };

  setWinner = (name, id, csrfToken) => {
    this.raffle.winner = name;
    this.raffle.winnerId = id;
    this.updateRaffleList(csrfToken);
  };

  setWinners = list => {
    this.raffle.winners = list;
  };

  setRaffleState = state => (this.raffle.spinning = state);

  getAdmin = async id => {
    try {
      let { data } = await axios.get(`/api/v1/host/${id}`);
      return data.data;
    } catch (error) {
      return false;
    }
  };

  setHack = state => (this.hackTheStream = state);

  setPollID = id => (this.fundky.pollid = id);

  setFrameID = id => (this.fundky.frameid = id);

  setFrameMan = name => (this.frameType = name);

  hasInput = inputName => {
    if (!inputName) return false;
    return Array.isArray(this.obs.inputs) && this.obs.inputs.includes(inputName);
  };

  setBackgroundMan = name => {
    this.fundky.background = name;
    const test = `!BG : ${name}`;
    const sources = this.backgroundSourceNames.length ? this.backgroundSourceNames : [test];
    sources.forEach(source => {
      this.setSceneItemEnabled('!BACKGROUNDS', source, source === test);
    });
  };

  getSceneItemId = async (sceneName, sourceName) => {
    if (!this.obs.socket || !sceneName || !sourceName) {
      return null;
    }
    const cacheKey = `${sceneName}::${sourceName}`;
    const cached = this.obs.sceneItemIdCache[cacheKey];
    if (cached !== undefined) {
      return cached;
    }
    try {
      const { sceneItemId } = await this.obs.socket.call('GetSceneItemId', {
        sceneName,
        sourceName,
      });
      this.obs.sceneItemIdCache[cacheKey] = sceneItemId;
      return sceneItemId;
    } catch (error) {
      console.error('Failed to resolve scene item id', { sceneName, sourceName, error });
      return null;
    }
  };

  getSceneItemEnabled = async (sceneName, sourceName) => {
    const sceneItemId = await this.getSceneItemId(sceneName, sourceName);
    if (sceneItemId === null) {
      return null;
    }
    const { sceneItemEnabled } = await this.obs.socket.call('GetSceneItemEnabled', {
      sceneName,
      sceneItemId,
    });
    return sceneItemEnabled;
  };

  setSceneItemEnabled = async (sceneName, sourceName, enabled) => {
    const sceneItemId = await this.getSceneItemId(sceneName, sourceName);
    if (sceneItemId === null) {
      return null;
    }
    return this.obs.socket.call('SetSceneItemEnabled', {
      sceneName,
      sceneItemId,
      sceneItemEnabled: enabled,
    });
  };

  setCurrentSceneItemEnabled = (sourceName, enabled) => {
    const sceneName = this.obs.currentScene;
    if (!sceneName) {
      return null;
    }
    return this.setSceneItemEnabled(sceneName, sourceName, enabled);
  };

  setBackground = () => {
    const test = `!BG : ${this.fundky.background}`;
    const sources = this.backgroundSourceNames.length ? this.backgroundSourceNames : [test];
    sources.forEach(source => {
      this.setSceneItemEnabled('!BACKGROUNDS', source, source === test);
    });
  };

  startRecording = () => {
    if (this.obs.socket) {
      this.obs.socket.call('StartRecord');
    }
  };

  stopRecording = () => {
    if (this.obs.socket) {
      this.obs.socket.call('StopRecord');
      setTimeout(() => this.obs.socket.call('StartRecord'), 5000);
    }
  };

  tiltify = {
    id: '130283',
    amount: 0.0,
    polls: [],
    milestones: [],
    donations: [],
    donationStatus: {},
    donationsHidden: {},
    bidwarStatus: {},
    milestoneStatus: [],
  };

  adIndex = 0;

  setGroup = (isGroupB, idx) => {
    if (!isGroupB) {
      this.obs.selected.current = idx;
    } else {
      this.obs.selected.next = idx;
    }
  };

  SetRTMPVisibility = flow(function* (idxA, idxB) {
    let prev;
    let next;

    // GETS RTMP SOURCES FOR SWAP
    for (const source of RTMPSources) {
      const currentVisible = yield this.getSceneItemEnabled(RTMPScenes[idxA], source);
      if (currentVisible) prev = source;

      const swapVisible = yield this.getSceneItemEnabled(RTMPScenes[idxB], source);
      if (swapVisible) next = source;
    }

    // SETS REPLACEMENT RTMP SOURCES
    for (const source of RTMPSources) {
      yield this.setSceneItemEnabled(RTMPScenes[idxA], source, source === next);
      yield this.setSceneItemEnabled(RTMPScenes[idxB], source, source === prev);
    }
  });

  TempSwap = (idxA, idxB) => {
    const items = this.horaroData.items;
    if (
      !Array.isArray(items) ||
      idxA < 0 ||
      idxB < 0 ||
      idxA >= items.length ||
      idxB >= items.length
    ) {
      return;
    }

    [items[idxA], items[idxB]] = [items[idxB], items[idxA]];

    const selectedIdx = this.selectedRunIdx;
    if (Array.isArray(items) && selectedIdx >= 0 && selectedIdx < items.length) {
      const run = items[selectedIdx];
      const rtmpNames = run.rtmp ? run.rtmp.split(', ') : ['none', 'none', 'none', 'none'];
      for (let i = 0; i < rtmpNames.length; i++) {
        const sourceSettings = {
          buffering_mb: 1,
          clear_on_media_end: true,
          input: this.rtmpURL + rtmpNames[i],
          input_format: 'rtmp',
          is_local_file: false,
          restart_on_activate: false,
        };
        const name = `RTMP0${i + 1}`;
        if (this.hasInput(name)) {
          this.obs.socket.call('SetInputSettings', {
            inputName: name,
            inputSettings: sourceSettings,
          });
        }
      }
    }
  };

  SwitchRTMP = (idxA, idxB) => {
    const items = this.horaroData.items;
    if (
      !Array.isArray(items) ||
      idxA < 0 ||
      idxB < 0 ||
      idxA >= items.length ||
      idxB >= items.length
    ) {
      return;
    }

    [this.runners[idxA], this.runners[idxB]] = [this.runners[idxB], this.runners[idxA]];
    [this.obs.rtmp[idxA], this.obs.rtmp[idxB]] = [this.obs.rtmp[idxB], this.obs.rtmp[idxA]];
    this.setRunnersT(idxA, this.runners[idxA].name);
    this.setRunnersT(idxB, this.runners[idxB].name);
    this.SetRTMPVisibility(idxA, idxB);
  };

  setAdIndex = id => {
    this.adIndex = id;
    this.setAd(this.adIndex);
  };

  setAd = id => {
    this.obs.ad.forEach((data, index) => {
      data.muted = index !== id;
      data.visible = index !== id;
    });
    if (this.obs.ads !== undefined) {
      this.obs.ads.forEach((source, index) => {
        this.setSceneItemEnabled('!Ads', source, index === id);
      });
    }
  };

  setTiltifyID = id => (this.tiltify.id = id);
  setFundkyID = id => (this.fundky.id = id);
  setOBSIP = ip => (this.test.ip = ip);
  setOBSPort = port => (this.test.port = port);

  currentRTMP = 0;
  currentRTMP2 = 4;

  setTimerOffset = (name, number) => {
    this.timerOffset[name] = number;
  };

  setRTMPAudio = index => {
    this.currentRTMP = index;
  };

  setRTMPAudioB = index => {
    this.currentRTMP2 = index;
  };

  setRunnerCount = playerList => {
    this.runnerCount = playerList.length;
  };

  toggleLanguage = () => {
    this.lang = this.lang === 'EN' ? 'FR' : 'EN';
  };

  toggleUndo = () => {
    this.fundky.milestoneStatus.pop();
  };

  sendCurrent = () => {
    const items = this.horaroData.items;
    if (
      !Array.isArray(items) ||
      items.length === 0 ||
      this.selectedRunIdx < 0 ||
      this.selectedRunIdx >= items.length
    ) {
      return;
    }
    const run = items[this.selectedRunIdx];

    // NEW METHOD
    this.currentGame.title = run.game;
    this.currentGame.subtitle = run.subtitle || 'Game Subtitle';
    this.currentGame.category = run.category;
    this.currentGame.platform = run.platform;
    this.currentGame.extra = run.extra || '';
    this.currentGame.estimate = run.estimate;

    // OLD METHOD
    this.gameName = run.game;
    this.gameSubtitle = run.subtitle || '';
    this.category = run.category;
    this.platform = run.platform;
    this.extraInformation = run.extrainfo || '';
    this.estimate = run.estimate;
    // this.estimate2 =
    //   items.length - 1 > this.selectedRunIdx
    //     ? items[this.selectedRunIdx + 1].length_formated
    //     : '00:00:00';

    this.host = run.host;
    this.couch = run.couch;

    const runnerNames = run.runners ? run.runners.split(',') : [];
    this.setRunnerCount(runnerNames);
    this.players = runnerNames.length;

    // Set names for available runners
    for (let i = 0; i < this.runners.length; i++) {
      if (i < runnerNames.length) {
        this.runners[i].name = runnerNames[i].trim();
      } else {
        this.runners[i].name = ''; // Reset extra runners
      }
    }

    // const rtmpNames = run.rtmp ? run.rtmp.split(', ') : ['none', 'none', 'none', 'none'];
    // for (let j = 0; j < rtmpNames.length; j++) {
    //   const sourceSettings = {
    //     buffering_mb: 1,
    //     clear_on_media_end: true,
    //     input: this.rtmpURL + rtmpNames[j],
    //     input_format: 'rtmp',
    //     is_local_file: false,
    //     restart_on_activate: false,
    //   };
    //   const name = `RTMP0${j + 1}`;
    //   this.obs.socket.send('SetSourceSettings', {
    //     sourceName: name,
    //     sourceSettings,
    //   });
    // }
  };

  setRunnersT = (idx, name) => {
    const items = this.horaroData.items;
    if (
      !Array.isArray(items) ||
      items.length === 0 ||
      this.selectedRunIdx < 0 ||
      this.selectedRunIdx >= items.length
    ) {
      return;
    }
    switch (idx) {
      case 0:
        this.horaroData.items[this.selectedRunIdx].player01 = name;
        break;
      case 1:
        this.horaroData.items[this.selectedRunIdx].player02 = name;
        break;
      case 2:
        this.horaroData.items[this.selectedRunIdx].player03 = name;
        break;
      case 3:
        this.horaroData.items[this.selectedRunIdx].player04 = name;
        break;
      case 4:
        this.horaroData.items[this.selectedRunIdx].player05 = name;
        break;
      case 5:
        this.horaroData.items[this.selectedRunIdx].player06 = name;
        break;
      case 6:
        this.horaroData.items[this.selectedRunIdx].player07 = name;
        break;
      case 7:
        this.horaroData.items[this.selectedRunIdx].player08 = name;
        break;
      default:
        break;
    }
  };

  sendCurrentT = () => {
    const items = this.horaroData.items;
    if (
      !Array.isArray(items) ||
      items.length === 0 ||
      this.selectedRunIdx < 0 ||
      this.selectedRunIdx >= items.length
    ) {
      return;
    }
    const run = items[this.selectedRunIdx];

    this.currentGame.title = run.game;
    this.currentGame.category = run.category;
    this.currentGame.platform = run.platform;
    this.currentGame.extra = run.extrainfo || '';
    this.currentGame.estimate = run.estimate;

    this.estimate = run.estimate;
    this.gameName = run.game;
    this.category = run.category;
    this.platform = run.platform;
    this.host = run.host;
    this.couch = run.couch;

    const runnerNames = run.runners ? run.runners.split(', ') : [];
    this.setRunnerCount(runnerNames);
    this.players = runnerNames.length;
    for (let i = 0; i < runnerNames.length; i++) {
      this.runners[i].name = runnerNames[i];
    }

    this.extraInformation = run.extrainfo || '';
    const rtmpNames = run.rtmp
      ? run.rtmp.split(', ')
      : ['none', 'none', 'none', 'none', 'none', 'none', 'none', 'none'];
    for (let j = 0; j < rtmpNames.length; j++) {
      const sourceSettings = {
        buffering_mb: 1,
        clear_on_media_end: true,
        input: this.rtmpURL + rtmpNames[j],
        input_format: 'rtmp',
        is_local_file: false,
        restart_on_activate: false,
      };
      const name = `RTMP0${j + 1}`;
      if (this.hasInput(name)) {
        this.obs.socket.call('SetInputSettings', {
          inputName: name,
          inputSettings: sourceSettings,
        });
      }
    }
  };

  sendNext = () => {
    const items = this.horaroData.items;
    if (
      !Array.isArray(items) ||
      items.length === 0 ||
      this.selectedRunIdx < 0 ||
      this.selectedRunIdx >= items.length
    ) {
      return;
    }

    this.upcomingData.items = [...items];
    this.currentRunIdx = this.selectedRunIdx;

    // NEW METHOD
    const [game0, game1] = this.nextGame.slice(0, 2);
    game0.title = items[this.selectedRunIdx].game;
    game0.subtitle = items[this.selectedRunIdx].subtitle;
    game0.category = items[this.selectedRunIdx].category;
    game0.platform = items[this.selectedRunIdx].platform;

    if (items.length > this.selectedRunIdx + 1) {
      game1.title = items[this.selectedRunIdx + 1].game;
      game1.subtitle = items[this.selectedRunIdx + 1].subtitle;
      game1.category = items[this.selectedRunIdx + 1].category;
      game1.platform = items[this.selectedRunIdx + 1].platform;
    } else {
      game1.title = 'Next Game 2';
      game1.subtitle = 'Next Subtitle 2';
      game1.category = 'Next Category 2';
      game1.platform = 'Next Platform 2';
    }
    // Rebuild array AND indexed references to make sure the state updates
    this.nextGame = [{ ...game0 }, { ...game1 }, ...this.nextGame.slice(2)];

    // OLD METHOD
    this.nextGameName = items[this.selectedRunIdx].game;
    this.nextGameSubtitle = items[this.selectedRunIdx].subtitle;
    this.nextCategory = items[this.selectedRunIdx].category;
    this.nextPlatform = items[this.selectedRunIdx].platform;

    if (items.length > this.selectedRunIdx + 1) {
      this.nextGameName2 = items[this.selectedRunIdx + 1].game;
      this.nextCategory2 = items[this.selectedRunIdx + 1].category;
      this.nextPlatform2 = items[this.selectedRunIdx + 1].platform;
    } else {
      this.nextGameName2 = 'Next Game';
      this.nextCategory2 = 'Next Category';
      this.nextPlatform2 = 'Next Platform';
    }

    this.nextHost = items[this.selectedRunIdx].host;
    this.nextCouch = items[this.selectedRunIdx].couch;

    this.nextRunner = items[this.selectedRunIdx].runners;
    this.nextRunner2 =
      items.length > this.selectedRunIdx + 1
        ? items[this.selectedRunIdx + 1].runners
        : 'Next Runner';
  };

  sendNextT = () => {
    const items = this.horaroData.items;
    if (
      !Array.isArray(items) ||
      items.length === 0 ||
      this.selectedRunIdx < 0 ||
      this.selectedRunIdx >= items.length
    ) {
      return;
    }
    this.nextGameName = items[this.selectedRunIdx].game;
    this.nextCategory = items[this.selectedRunIdx].category;
    this.nextPlatform = items[this.selectedRunIdx].platform;
    this.nextHost = items[this.selectedRunIdx].host;
    this.nextCouch = items[this.selectedRunIdx].couch;
    this.nextRunner = items[this.selectedRunIdx].runners;
  };

  getPrev = () => {
    const items = this.horaroData.items;
    if (!Array.isArray(items) || items.length === 0) {
      return;
    }
    this.selectedRunIdx = this.selectedRunIdx === 0 ? items.length - 1 : this.selectedRunIdx - 1;
  };

  getNext = () => {
    const items = this.horaroData.items;
    if (!Array.isArray(items) || items.length === 0) {
      return;
    }
    this.selectedRunIdx = this.selectedRunIdx === items.length - 1 ? 0 : this.selectedRunIdx + 1;
  };

  startTimer = flow(function* () {
    const timestamp = yield getTime();
    const hoursInMilliseconds = this.timerOffset.hours * 60 * 60 * 1000;
    const minutesInMilliseconds = this.timerOffset.minutes * 60 * 1000;
    const secondsInMilliseconds = this.timerOffset.seconds * 1000;
    const offset = hoursInMilliseconds + minutesInMilliseconds + secondsInMilliseconds;

    this.timer.start = this.timer.start || timestamp - offset;
    this.timer.end = null;
    const items = this.horaroData.items;
    if (Array.isArray(items) && this.selectedRunIdx >= 0 && this.selectedRunIdx < items.length) {
      const run = items[this.selectedRunIdx];
      const id = run?.id;
      if (id) axios.post(`${WEBSITE_API_URL}/api/v2/run/started/${id}/`);
    }
  });

  continueTimer = () => {
    this.timer.end = null;
  };

  startCountdown = flow(function* () {
    const timestamp = yield getTime();
    this.cdtimer.start = this.cdtimer.start || timestamp;
    this.cdtimer.end = timestamp + this.countdown.minutes * 60000;
  });

  resetCountdown = () => {
    this.cdtimer.start = null;
    this.cdtimer.end = null;
  };

  getItems = () => {
    const eventId = this.fundky?.id ?? '';
    if (eventId === '' || eventId == null) {
      this.horaroData = { items: [] };
      return;
    }
    axios
      .get(`/api/schedule`, { params: { event: eventId } })
      .then(res => {
        const runs = res.data.runs || [];

        // Map runs to the format you want
        const items = runs.map(run => ({
          id: run.id,
          game: run.gameName,
          category: run.category,
          platform: run.platform,
          estimate: run.estimate,
          runners: [run.player1, run.player2, run.player3, run.player4].filter(Boolean).join(', '),
          couch: [run.couch1, run.couch2].filter(Boolean),
          host: run.host,
          index: run.index,
          setupTime: run.setupTime,
          startedAt: run.startedAt,
          endedAt: run.endedAt,
          createdAt: run.createdAt,
          updatedAt: run.updatedAt,
          submission: run.submission,
          schedule: run.schedule,
          extra: '',
        }));

        this.horaroData = { items };

        // If we have at least one run, set runners
        // if (items.length > 0) {
        //   items[0].runners.forEach((player, i) => {
        //     this.setRunnersT(i, player);
        //   });
        // }
      })
      .catch(() => {
        this.horaroData = { items: [] };
      });
  };

  getEventData = async () => {
    try {
      const params = this.fundky?.id != null ? { event: this.fundky.id } : {};
      const [oesResponse, volRetroResponse] = await Promise.all([
        axios.get('/api/oes', { params }),
        axios.get('https://volwrath.com/challenge3/top3.php'),
      ]);
      this.volRetroTopPlayers = volRetroResponse.data;
      this.fundky.polls = oesResponse.data.bidwars || [];
      this.fundky.milestones = oesResponse.data.milestones || [];
      this.fundky.donations = oesResponse.data.donations || [];
      let newTotal = oesResponse.data.donationTotal || 0.0;
      this.fundky.amount = newTotal;
      this.donationCount.lastDonation = newTotal - this.donationCount.currentTotal;
      this.donationCount.previousTotal = this.donationCount.currentTotal;
      this.donationCount.currentTotal = newTotal;
      this.lastEventUpdate = Date.now();

      if (
        this.hackTheStream &&
        this.obs?.socket &&
        this.fundky?.pollid != null &&
        this.fundky?.frameid != null
      ) {
        const bidwars = this.fundky.polls || [];
        const bgLabel = getLeadingBidOptionLabel(bidwars, this.fundky.pollid);
        if (bgLabel != null) {
          this.fundky.background = normalizeLabelForBg(bgLabel);
          this.setBackground();
        }
        const frameLabel = getLeadingBidOptionLabel(bidwars, this.fundky.frameid);
        if (frameLabel != null) {
          this.frameType = normalizeLabelForBg(frameLabel);
        }
      }
    } catch (error) {
      console.error("ERROR COULDN'T FETCH EVENT DATA", error.message);
    }
  };

  getSponsors = async () => {
    try {
      const response = await axios.get(`${WEBSITE_API_URL}/api/v2/sponsors`);
      if (response.status === 200) {
        this.sponsors = response.data || [];
      }
    } catch (error) {
      this.sponsors = [];
      console.error("ERROR COULDN'T FETCH SPONSORS", error.message);
    }
  };

  stopTimer = flow(function* () {
    if (!this.timerStarted) {
      return;
    }
    const timestamp = yield getTime();
    this.timer = { ...this.timer, end: timestamp };
    const items = this.horaroData.items;
    if (Array.isArray(items) && this.selectedRunIdx >= 0 && this.selectedRunIdx < items.length) {
      const { id } = items[this.selectedRunIdx] || {};
      if (id) axios.post(`${WEBSITE_API_URL}/api/v2/run/ended/${id}/`);
    }
  });

  resetTimer = () => {
    this.runners.forEach(r => {
      r.endTime = null;
    });
    this.timer = { start: null, end: null };
    this.timerOffset = { hours: 0, minutes: 0, seconds: 0 };
  };

  get upcomingRuns() {
    const items = this.upcomingData.items;
    if (!Array.isArray(items)) {
      return '';
    }
    const res = [];
    for (const horaroEntry of items.slice(this.currentRunIdx, this.currentRunIdx + 4)) {
      const { game, category, platform, runners, pollid, length_formated, milestoneid } =
        horaroEntry;
      res.push({ game, category, platform, runners, pollid, length_formated, milestoneid });
    }
    return res;
  }

  get cdtimerStarted() {
    return Boolean(this.cdtimer.start);
  }

  get cdtimerCompleted() {
    return Boolean(this.cdtimer.start) && Boolean(this.cdtimer.end);
  }

  get timerStarted() {
    return Boolean(this.timer.start);
  }

  get timerCompleted() {
    return Boolean(this.timer.start) && Boolean(this.timer.end);
  }

  get jsonString() {
    return JSON.stringify(toJS(this));
  }

  get currentlySelectedRun() {
    const items = this.horaroData.items;
    const fallbackRun = {
      id: null,
      game: this.currentGame.title,
      subtitle: this.currentGame.subtitle,
      category: this.currentGame.category,
      platform: this.currentGame.platform,
      estimate: this.currentGame.estimate,
      extrainfo: this.currentGame.extra,
      host: this.host,
      couch: this.couch,
      runners: this.runners
        .map(r => r.name)
        .filter(Boolean)
        .join(', '),
      rtmp: this.rtmpURL,
    };
    if (
      this.selectedRunIdx === null ||
      !Array.isArray(items) ||
      this.selectedRunIdx < 0 ||
      this.selectedRunIdx >= items.length
    ) {
      if (Array.isArray(items) && items.length === 0) {
        this.horaroData.items = [fallbackRun];
        this.selectedRunIdx = 0;
      }
      return fallbackRun;
    }
    return items[this.selectedRunIdx] || fallbackRun;
  }

  get plain() {
    return toJS(this);
  }

  get stringified() {
    return stringify(this.plain, {
      replacer: (k, v) => {
        if (filteredKeys.has(k)) {
          return undefined;
        }
        return v;
      },
    });
  }

  get hash() {
    return hash(this.stringified);
  }
}

export default Store;
